import React, { useState } from "react";
import { Clock, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ConsultationTimelineProps,
  useAuditTimelineData,
} from "@/lib/consultation/AuditTimelineData";
import { Skeleton } from "@mui/material";

export function ConsultationTimeline({
  isDarkMode = false,
  className = "p-5",
}: ConsultationTimelineProps) {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const {
    events,
    getActorIcon,
    getEventTypeColor,
    auditTimelineLoader,
    formatSummaryText,
  } = useAuditTimelineData();
  // Always show all events; parent handles scrolling
  const visibleEvents = events;
  const toggleEventExpansion = (eventId: string) => {
    const newExpandedEvents = new Set(expandedEvents);
    if (newExpandedEvents.has(eventId)) {
      newExpandedEvents.delete(eventId);
    } else {
      newExpandedEvents.add(eventId);
    }
    setExpandedEvents(newExpandedEvents);
  };

  return (
    <>
      {auditTimelineLoader ? (
        Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className={`flex items-center gap-3 p-3 m-3 border rounded-xl ${
              isDarkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-white border-gray-200"
            }`}
          >
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{
                bgcolor: isDarkMode ? "grey.800" : "grey.200",
              }}
            />
            <div className="flex-1">
              <Skeleton
                variant="rounded"
                height={14}
                width="60%"
                sx={{
                  bgcolor: isDarkMode ? "grey.800" : "grey.200",
                  borderRadius: "6px",
                }}
              />
              <Skeleton
                variant="rounded"
                height={12}
                width="40%"
                sx={{
                  bgcolor: isDarkMode ? "grey.800" : "grey.200",
                  borderRadius: "6px",
                  mt: 0.5,
                }}
              />
            </div>
            <Skeleton
              variant="rounded"
              height={20}
              width={60}
              sx={{
                bgcolor: isDarkMode ? "grey.800" : "grey.200",
                borderRadius: "10px",
              }}
            />
          </div>
        ))
      ) : (
        <div className={`space-y-4 ${className}`}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock
                className={`w-4 h-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              />
              <h4
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Timeline of Events
              </h4>
            </div>
            <Badge variant="secondary" className="text-xs">
              {events?.length} events
            </Badge>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div
              className={`absolute left-6 top-0 bottom-0 w-px ${
                isDarkMode ? "bg-gray-600" : "bg-gray-300"
              }`}
            />

            {/* Events */}
            <div className={"space-y-4"}>
              {visibleEvents?.map((event, index) => {
                const ActorIcon = getActorIcon(event?.actor);
                const isLast = index === visibleEvents?.length - 1;

                return (
                  <div key={event?.id} className="relative flex gap-4">
                    {/* Timeline dot */}
                    <div
                      className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <ActorIcon
                        className={`w-4 h-4 ${
                          event?.actor === "patient"
                            ? isDarkMode
                              ? "text-blue-300"
                              : "text-blue-600"
                            : isDarkMode
                              ? "text-green-300"
                              : "text-green-600"
                        }`}
                      />
                    </div>

                    {/* Event content */}
                    <div
                      className={`flex-1 pb-4 ${!isLast ? "border-b" : ""} ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          {/* Event header */}
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`text-sm font-medium ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {event.event}
                            </span>
                            <Badge
                              variant="outline"
                              className={`text-xs border ${getEventTypeColor(
                                event?.type,
                                event?.actor,
                              )}`}
                            >
                              {event.type === "Action 11" ||
                              event.type === "Action 12"
                                ? "Draft"
                                : event.type}
                            </Badge>
                          </div>

                          {/* Actor and description */}
                          <div
                            className={`text-xs mb-1 ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            <span className="font-medium">
                              {event?.actorName}
                            </span>
                            {event?.actor === "patient" && " (Patient)"}
                            {event?.actor === "pharmacist" && " (Pharmacist)"}
                          </div>

                          {event?.description &&
                            (event?.event === "Consultation Summary" ? (
                              <pre
                                className={`text-xs leading-relaxed whitespace-pre-wrap ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                                style={{ margin: 0, fontFamily: "inherit" }}
                              >
                                {formatSummaryText(event?.description)}
                              </pre>
                            ) : (
                              <p
                                className={`text-xs leading-relaxed ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                {formatSummaryText(event?.description)}
                              </p>
                            ))}

                          {/* Expandable Transcription for Consultation Started */}
                          {event?.event === "Consultation started" &&
                            event?.transcription && (
                              <div className="mt-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    toggleEventExpansion(event?.id)
                                  }
                                  className="flex items-center h-auto gap-1 p-1 text-xs"
                                >
                                  <FileText className="w-3 h-3" />
                                  {expandedEvents?.has(event?.id) ? (
                                    <>
                                      <ChevronUp className="w-3 h-3" />
                                      Hide transcription
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-3 h-3" />
                                      View transcription
                                    </>
                                  )}
                                </Button>

                                {expandedEvents?.has(event.id) && (
                                  <div
                                    className={`mt-2 p-3 rounded-lg border text-xs leading-relaxed max-h-60 overflow-y-auto scrollbar-hover-thin ${
                                      isDarkMode
                                        ? "bg-gray-800 border-gray-600 text-gray-200"
                                        : "bg-gray-50 border-gray-200 text-gray-700"
                                    }`}
                                  >
                                    <div
                                      className={`text-xs font-medium mb-2 ${
                                        isDarkMode
                                          ? "text-gray-300"
                                          : "text-gray-600"
                                      }`}
                                    >
                                      Consultation Transcription:
                                    </div>
                                    <div className="whitespace-pre-line">
                                      {event?.transcription}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                        </div>

                        {/* Time */}
                        <div
                          className={`text-right flex-shrink-0 text-xs ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <div className="font-medium">{event?.time}</div>
                          <div>{event?.date}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
